import { useCallback, useEffect, useRef } from "react";
import type { HelpWidgetMessage } from "../types/help_widget_message";

export const useThinkingQueue = (
  copilotLoadingText: string | null,
  addMessage: (message: HelpWidgetMessage) => void,
) => {
  const isThinkingQueueActiveRef = useRef(false);
  const pendingMessagesRef = useRef<HelpWidgetMessage[]>([]);

  useEffect(() => {
    if (copilotLoadingText && !isThinkingQueueActiveRef.current) {
      isThinkingQueueActiveRef.current = true;
    }
  }, [copilotLoadingText]);

  useEffect(
    () => () => {
      if (pendingMessagesRef.current.length > 0) {
        pendingMessagesRef.current.forEach(message => addMessage(message));
        pendingMessagesRef.current = [];
      }
      isThinkingQueueActiveRef.current = false;
    },
    [addMessage],
  );

  const handleQueueComplete = useCallback(() => {
    isThinkingQueueActiveRef.current = false;

    if (pendingMessagesRef.current.length > 0) {
      pendingMessagesRef.current.forEach(message => addMessage(message));
      pendingMessagesRef.current = [];
    }
  }, [addMessage]);

  const handleIncomingMessage = useCallback(
    (message: HelpWidgetMessage) => {
      if (!isThinkingQueueActiveRef.current) {
        addMessage(message);
        return;
      }

      pendingMessagesRef.current = [...pendingMessagesRef.current, message];
    },
    [addMessage],
  );

  return {
    handleIncomingMessage,
    handleQueueComplete,
    isThinkingQueueActiveRef,
  };
};
